// src/components/LoginPage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Briefcase, Shield, Settings, User } from 'lucide-react';

const LoginPage = () => {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [userType, setUserType] = useState(''); // 'production', 'manager', 'admin'
  const navigate = useNavigate();

  // Mots de passe codés en dur (à remplacer par une solution plus sécurisée en production)
  const passwords = {
    'production123': 'production',
    'manager456': 'manager',
    'admin789': 'admin'
  };

  // Vérifier si l'utilisateur est déjà authentifié
  useEffect(() => {
    const userType = localStorage.getItem('userType');
    if (userType) {
      navigate('/');
    }
  }, [navigate]);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Vérifier le mot de passe
    const authenticatedType = passwords[password];
    
    if (authenticatedType) {
      // Stocker le type d'utilisateur pour la session
      localStorage.setItem('userType', authenticatedType);
      setUserType(authenticatedType);
      
      // Rediriger vers la page appropriée
      navigate('/');
    } else {
      setError('Mot de passe incorrect');
      setTimeout(() => setError(''), 3000);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-blue-900 to-blue-700">
      {/* Header avec logo */}
      <header className="p-6 flex justify-center">
        <img src="/logo_bleu.png" alt="PolyPlan" className="h-32" />
      </header>
      
      {/* Contenu principal */}
      <main className="flex-grow flex items-center justify-center px-4">
        <div className="max-w-4xl w-full flex flex-col md:flex-row">
          {/* Section de gauche : Présentation */}
          <div className="bg-black bg-opacity-30 p-8 rounded-t-lg md:rounded-l-lg md:rounded-tr-none text-white md:w-3/5">
            <h1 className="text-3xl font-bold mb-4">PolyPlan</h1>
            <h2 className="text-xl text-blue-300 mb-6">Solution de planification de vos moyens chauds</h2>
            
            <div className="space-y-6">
              <div className="flex items-start">
                <div className="bg-blue-600 p-2 rounded-full mr-4">
                  <Briefcase size={24} />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">Gestion optimisée des OF</h3>
                  <p className="text-gray-300">Planifiez et suivez vos ordres de fabrication en temps réel avec une interface intuitive.</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="bg-blue-600 p-2 rounded-full mr-4">
                  <Shield size={24} />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">Suivi de production</h3>
                  <p className="text-gray-300">Visualisez l'état d'avancement de vos fournées et la charge de vos moyens chauds.</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="bg-blue-600 p-2 rounded-full mr-4">
                  <Settings size={24} />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">Configuration flexible</h3>
                  <p className="text-gray-300">Adaptez les cycles de polymérisation et les paramètres des autoclaves et étuves selon vos besoins.</p>
                </div>
              </div>
            </div>
            
            <div className="mt-8 pt-6 border-t border-blue-600">
              <p className="text-sm text-gray-400">Version de démonstration à des fins d'évaluation uniquement.</p>
            </div>
          </div>
          
          {/* Section de droite : Connexion */}
          <div className="bg-white p-8 rounded-b-lg md:rounded-r-lg md:rounded-bl-none shadow-lg md:w-2/5">
            <div className="flex justify-center mb-6">
              <div className="bg-blue-100 p-3 rounded-full">
                <User size={32} className="text-blue-600" />
              </div>
            </div>
            
            <h2 className="text-xl font-bold text-center mb-6">Connexion</h2>
            
            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                  Mot de passe
                </label>
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Entrez votre mot de passe"
                  required
                />
              </div>
              
              {error && (
                <div className="mb-4 p-2 bg-red-100 text-red-700 rounded text-sm">
                  {error}
                </div>
              )}
              
              <button
                type="submit"
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Se connecter
              </button>
            </form>
            
            <div className="mt-6 text-center text-sm text-gray-500">
              <p>Pour accéder à la démonstration, utilisez l'un des accès fournis.</p>
            </div>
          </div>
        </div>
      </main>
      
      {/* Footer avec logo de l'éditeur */}
      <footer className="p-6 text-center text-white">
        <p className="mb-2 text-xs text-gray-300">© 2025 PolyPlan - Application de planification des moyens chauds</p>
        <div className="flex justify-center items-center">
          <span className="text-xs text-gray-400 mr-2">Développé par</span>
          <img src="/logo_KaiDaTa.png" alt="KaiDaTa" className="h-6" />
        </div>
      </footer>
    </div>
  );
};

export default LoginPage;